'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';

/**
 * New Relic RUM (Real User Monitoring) Script Component
 *
 * Loads the appropriate New Relic browser agent based on the environment.
 * Uses NEXT_PUBLIC_NEW_RELIC_APP_ID to determine which script to load.
 *
 * Environments:
 * - Stage: applicationID="538810625" (stage.toniagent.com)
 * - Production: applicationID="538810627" (app.toniagent.com)
 *
 * Scripts are loaded from /public/scripts/ to avoid syntax conflicts
 *
 * The loaders set `privacy: { cookies_enabled: false }`, so the agent writes nothing to
 * cookies, localStorage or sessionStorage (measured, not assumed). That is what keeps it
 * out of consent territory; in exchange it cannot group page views by itself, so
 * `userId` supplies the attribution instead.
 *
 * Mounted only through `PerformanceMonitoring`, i.e. for a signed-in user who has not
 * objected. That is why it no longer lives in the root layout `<head>` with
 * `beforeInteractive`.
 *
 * IMPORTANT: Sets applicationVersion for source map matching
 * The version must match the --releaseName used when uploading source maps
 */
export default function NewRelicScript({ userId }: { userId?: string }) {
  const appId = process.env.NEXT_PUBLIC_NEW_RELIC_APP_ID;
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || 'unknown';
  // The agent reads NREUM.info at load time, so the version is written first and
  // the agent script is only rendered once that has happened.
  const [versionReady, setVersionReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as unknown as { NREUM?: { info?: Record<string, unknown> } };
    w.NREUM = w.NREUM || {};
    w.NREUM.info = w.NREUM.info || {};
    w.NREUM.info.applicationVersion = appVersion;
    setVersionReady(true);

    if (process.env.NODE_ENV !== 'production') {
      console.log('New Relic application version set:', appVersion);
    }
  }, [appVersion]);

  // Attribution, in place of the session identifier the agent no longer stores. The
  // Cognito subject is opaque; the user's email is deliberately never sent.
  useEffect(() => {
    if (!userId || typeof window === 'undefined') return;
    const agent = (window as unknown as { newrelic?: { setUserId?: (id: string) => void } }).newrelic;
    agent?.setUserId?.(userId);
  }, [userId, versionReady]);

  if (process.env.NODE_ENV === 'development') {
    return null;
  }

  const scriptSrc = appId === '538810625'
    ? '/scripts/newrelic-stage.js'
    : appId === '538810627'
    ? '/scripts/newrelic-production.js'
    : null;

  if (!scriptSrc || !versionReady) {
    return null;
  }

  return (
    <Script
      id="newrelic-agent"
      src={scriptSrc}
      strategy="afterInteractive"
      onReady={() => {
        if (!userId) return;
        const agent = (window as unknown as { newrelic?: { setUserId?: (id: string) => void } }).newrelic;
        agent?.setUserId?.(userId);
      }}
    />
  );
}
